import { useState, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  InputAdornment,
  Stack,
  Link,
  Snackbar,
  Alert as MuiAlert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import MonitorIcon from '@mui/icons-material/Monitor'
import StorageIcon from '@mui/icons-material/Storage'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import CheckIcon from '@mui/icons-material/Check'
import { useSnackbar } from 'notistack'
import { useAppTranslation } from 'src/admin/core/hooks'
import {
  fetchAgencyDisplayAssignments,
  upsertAgencyDisplays,
  AgencyData,
  deleteServerFingerprint,
} from 'src/admin/core/services/screens'
import { CircularProgress, Alert } from '@mui/material'
import { useScreensContext } from '../ScreensContext'
import { useAgencies } from 'src/admin/scenes/entities/agencies/hooks'
import { getAppBarState } from 'src/store'
import { generateFilterGraphql } from 'src/admin/core'

const AsociarDisplays = () => {
  const { label } = useAppTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const {
    setTabDirty,
    setHasValidationErrors,
    registerSaveHandler,
    screens,
    enterprise,
    loadingSharedData,
  } = useScreensContext()

  const { results: { results: agencyList }, loading: loadingAgencies, getAgencies } = useAgencies()
  const { userEnterprise } = getAppBarState().appState

  const [agencies, setAgencies] = useState<AgencyData[]>([])
  const originalAgencies = useRef<AgencyData[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const dirtySet = useRef<Set<string>>(new Set())
  const agenciesRef = useRef<AgencyData[]>([])
  const allAgencyDisplays = useRef<AgencyData[]>([])

  const [selectedAgencyId, setSelectedAgencyId] = useState<string>('')
  const [selectedServerIndex, setSelectedServerIndex] = useState<number>(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [disassociating, setDisassociating] = useState(false)
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  // Derived from shared context
  const baseUrl = enterprise?.base_url || ''
  const enterpriseSchedule = enterprise?.schedule || { startTime: '', endTime: '', timezone: '' }

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const refreshAssignments = async () => {
    const res = await fetchAgencyDisplayAssignments({}, userEnterprise.id)
    if (res.success && Array.isArray(res.data)) {
      allAgencyDisplays.current = res.data
      
      // Update any already-loaded agencies in state
      setAgencies((prev) => {
        const next = prev.map((agency) => {
          const fresh = res.data.find(a => a._id === agency._id)
          if (!fresh) return agency
          
          const gAgency = agencyList.find((a: any) => a.id === agency._id)
          const firstScreenId = screens.length > 0 ? screens[0]._id : ''
          
          return {
            ...fresh,
            _id: agency._id,
            name: gAgency?.name || fresh.name,
            servers: (fresh.servers || []).map((server) => ({
              ...server,
              displays: (server.displays || []).map((display) => ({
                ...display,
                screen_id: display.screen_id || firstScreenId,
              })).sort((a, b) => a.display - b.display),
            })),
          }
        })
        agenciesRef.current = next
        // After refresh, sync original to the new loaded state
        originalAgencies.current = JSON.parse(JSON.stringify(next))
        return next
      })
    }
  }

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      // 1. Load Agencies list via GraphQL
      getAgencies({
        where: {
          and: [
            { enterpriseId: { ...generateFilterGraphql(userEnterprise.id, 'eq') } },
            { name: { ...generateFilterGraphql('ONLINE_AGENCY', 'neq') } }
          ]
        }
      })

      // 2. Load agency display assignments
      await refreshAssignments()

    } catch (err: any) {
      setError(err.message || 'Error loading data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const save = async () => {
      const dirty = dirtySet.current
      if (dirty.size === 0) return
      
      // Validate all dirty agencies have valid data
      const dirtyList = agenciesRef.current.filter((a) => dirty.has(a._id))
      const invalidAgency = dirtyList.find((agency) =>
        agency.servers.some((server) =>
          server.active !== false && !server.name?.trim()
        )
      )
      if (invalidAgency) {
        setError(`La agencia "${invalidAgency.name}" tiene datos inválidos (nombre de servidor vacío). Corrija antes de guardar.`)
        return
      }
      await Promise.all(
        dirtyList.map((agency) =>
          upsertAgencyDisplays(
            {
              agency_id: agency._id,
              enterprise_id: userEnterprise.id,
              name: agency.name,
              active: agency.active,
              schedule: agency.schedule?.startTime ? agency.schedule : enterpriseSchedule,
              servers: agency.servers.map((server) => {
                  return {
                    ...(server.server_id ? { server_id: server.server_id } : {}),
                    name: server.name || '',
                    polling_seconds: server.polling_seconds || 30,
                    license_key: server.license_key,
                    active: server.active !== false,
                    displays: server.displays.map((d) => ({
                    display: d.display,
                    screen_url: d.screen_url,
                    screen_id: d.screen_id,
                    active: d.active !== false,
                  })),
                }
              }),
            },
            userEnterprise.id,
          ),
        ),
      )
      dirtySet.current = new Set()
      setTabDirty('displays', false)
      originalAgencies.current = JSON.parse(JSON.stringify(agenciesRef.current))
      
      // Refresh to pick up server-generated IDs/data
      await refreshAssignments()
      
      setSaveSuccess(true)
    }

    registerSaveHandler('displays', save)
  }, [registerSaveHandler, setTabDirty])

  const copyToClipboard = (text: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
      enqueueSnackbar(label('COPIADO'), { variant: 'success' })
    } else {
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      try {
        document.execCommand('copy')
        enqueueSnackbar(label('COPIADO'), { variant: 'success' })
      } catch (err) {
        enqueueSnackbar(label('ERROR_AL_COPIAR'), { variant: 'error' })
      }
      document.body.removeChild(textArea)
    }
  }

  const handleDisassociateServer = async () => {
    if (!selectedServer || !selectedServer.server_id || !selectedServer.license_key) return
    
    setDisassociating(true)
    try {
      const res = await deleteServerFingerprint(
        selectedServer.server_id,
        selectedServer.license_key,
        userEnterprise.id
      )
      if (res.success) {
        enqueueSnackbar(res.detail || 'El servidor fué reiniciado de manera exitosa.', { variant: 'success' })
        await refreshAssignments()
      }
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Error al desasociar el servidor', { variant: 'error' })
    } finally {
      setDisassociating(false)
    }
  }

  useEffect(() => { loadData() }, [])

  const updateAgencies = (updater: (prev: AgencyData[]) => AgencyData[]) => {
    setAgencies((prev) => {
      const next = updater(prev)
      agenciesRef.current = next
      return next
    })
  }

  // ─── Sync dirty state and validation to context ───
  useEffect(() => {
    // 1. Calculate validation errors (only server names are required)
    const hasErrors = agencies.some((agency) =>
      agency.servers.some((server) => server.active !== false && !server.name?.trim())
    )
    setHasValidationErrors(hasErrors)

    // 2. Calculate dirty agencies
    const nextDirtySet = new Set<string>()
    agencies.forEach((agency) => {
      const original = originalAgencies.current.find((a) => a._id === agency._id)
      if (JSON.stringify(original) !== JSON.stringify(agency)) {
        nextDirtySet.add(agency._id)
      }
    })
    
    dirtySet.current = nextDirtySet
    setTabDirty('displays', nextDirtySet.size > 0)
  }, [agencies, setHasValidationErrors, setTabDirty])

  // ─── handlers ───
  const handleAgencyClick = (agencyId: string) => {
    setSelectedAgencyId(agencyId)

    // If we already have the agency details loaded, just select it
    const existing = agencies.find(a => a._id === agencyId)
    if (existing) {
      const firstActiveIndex = existing.servers.findIndex(s => s.active !== false)
      setSelectedServerIndex(firstActiveIndex)
      return
    }

    // Look up from the pre-fetched agency display data
    const agencyDisplay = allAgencyDisplays.current.find(a => a._id === agencyId)
    const gAgency = agencyList.find(a => a.id === agencyId)
    const firstScreenId = screens.length > 0 ? screens[0]._id : ''

    if (agencyDisplay) {
      const processedAgency: AgencyData = {
        ...agencyDisplay,
        _id: agencyId,
        name: gAgency?.name || agencyDisplay.name,
        servers: (agencyDisplay.servers || []).map((server) => ({
          ...server,
          displays: (server.displays || []).map((display) => ({
            ...display,
            screen_id: display.screen_id || firstScreenId,
          })).sort((a, b) => a.display - b.display),
        })),
      }

      setAgencies(prev => [...prev, processedAgency])
      agenciesRef.current = [...agenciesRef.current, processedAgency]
      originalAgencies.current = [...originalAgencies.current, JSON.parse(JSON.stringify(processedAgency))]
      
      const firstActiveIndex = processedAgency.servers.findIndex(s => s.active !== false)
      setSelectedServerIndex(firstActiveIndex)
    } else {
      // No display config exists yet for this agency — create empty shell
      const emptyAgency: AgencyData = {
        _id: agencyId,
        name: gAgency?.name || 'Unknown',
        enterprise_id: userEnterprise.id,
        schedule: enterpriseSchedule,
        servers: []
      }
      setAgencies(prev => [...prev, emptyAgency])
      agenciesRef.current = [...agenciesRef.current, emptyAgency]
      originalAgencies.current = [...originalAgencies.current, JSON.parse(JSON.stringify(emptyAgency))]
      setSelectedServerIndex(-1)
    }
  }

  // ─── derived ───
  const selectedAgency = agencies.find((a) => a._id === selectedAgencyId)
  const selectedServer = selectedAgency?.servers[selectedServerIndex]

  useEffect(() => {
    if (agencyList.length > 0 && !selectedAgencyId) {
      handleAgencyClick(agencyList[0].id)
    }
  }, [agencyList, selectedAgencyId])

  const handleServerNameChange = (value: string) => {
    updateAgencies(
      (prev) =>
        prev.map((agency) =>
          agency._id !== selectedAgencyId
            ? agency
            : {
                ...agency,
                servers: agency.servers.map((server, sIdx) =>
                  sIdx !== selectedServerIndex ? server : { ...server, name: value },
                ),
              },
        )
    )
  }

  /** Add a new empty server to the selected agency and select it */
  const handleAddServer = () => {
    if (!selectedAgency) return
    const newServer = { name: '', polling_seconds: 30, license_key: '', active: true, displays: [] }
    // If the agency doesn't have a schedule yet, assign the enterprise default
    const needsSchedule = !selectedAgency.schedule?.startTime
    
    // Calculate new index before update to ensure focus
    const newIndex = selectedAgency.servers.length

    updateAgencies(
      (prev) =>
        prev.map((agency) =>
          agency._id !== selectedAgencyId
            ? agency
            : {
                ...agency,
                ...(needsSchedule ? { schedule: enterpriseSchedule } : {}),
                servers: [...agency.servers, newServer],
              },
        )
    )
    setSelectedServerIndex(newIndex)
  }

  const handleToggleAgency = (e: React.MouseEvent, agencyId: string) => {
    e.stopPropagation()
    
    // Check if it's already in the 'agencies' state (local edits)
    const existing = agencies.find(a => a._id === agencyId)
    
    if (existing) {
      const nextActive = existing.active === false ? true : false
      updateAgencies(
        (prev) => prev.map(a => a._id === agencyId ? { ...a, active: nextActive } : a)
      )
    } else {
      // If not in local edits, sync from ref or create shell
      const agencyDisplay = allAgencyDisplays.current.find(a => a._id === agencyId)
      const gAgency = agencyList.find((a: any) => a.id === agencyId)
      const firstScreenId = screens.length > 0 ? screens[0]._id : ''
      
      const baseAgency: AgencyData = agencyDisplay 
        ? { 
            ...agencyDisplay, 
            _id: agencyId, 
            name: gAgency?.name || agencyDisplay.name,
            servers: (agencyDisplay.servers || []).map((server) => ({
              ...server,
              displays: (server.displays || []).map((display) => ({
                ...display,
                screen_id: display.screen_id || firstScreenId,
              })).sort((a, b) => a.display - b.display),
            })),
          }
        : {
            _id: agencyId,
            name: gAgency?.name || 'Unknown',
            enterprise_id: userEnterprise.id,
            schedule: enterpriseSchedule,
            servers: [],
            active: false
          }
      
      // Setup original for dirty checking BEFORE updating state
      if (!originalAgencies.current.some(a => a._id === agencyId)) {
        originalAgencies.current = [...originalAgencies.current, JSON.parse(JSON.stringify(baseAgency))]
      }
          
      const nextActive = baseAgency.active === false ? true : false
      updateAgencies((prev) => [...prev, { ...baseAgency, active: nextActive }])
    }
  }

  const handleToggleServer = (e: React.MouseEvent, index: number) => {
    e.stopPropagation()
    if (!selectedAgency) return

    updateAgencies(
      (prev) =>
        prev.map((agency) =>
          agency._id !== selectedAgencyId
            ? agency
            : {
                ...agency,
                servers: agency.servers.map((s, sIdx) => 
                  sIdx !== index ? s : { ...s, active: s.active === false }
                ),
              },
        ),
    )
  }

  const handleRemoveDisplay = (displayIndex: number) => {
    if (!selectedServer || !selectedAgencyId) return

    updateAgencies(
      (prev) =>
        prev.map((agency) =>
          agency._id !== selectedAgencyId
            ? agency
            : {
                ...agency,
                servers: agency.servers.map((server, sIdx) =>
                  sIdx !== selectedServerIndex
                    ? server
                    : {
                        ...server,
                        displays: server.displays.map((d, dIdx) => 
                          dIdx !== displayIndex ? d : { ...d, active: d.active === false }
                        ),
                      },
                ),
              },
        ),
    )
  }

  /** Update a single field of a display row */
  const handleDisplayChange = (
    displayIndex: number,
    field: 'display' | 'screen_url' | 'screen_id',
    value: string | number,
  ) => {
    updateAgencies(
      (prev) =>
        prev.map((agency) =>
          agency._id !== selectedAgencyId
            ? agency
            : {
                ...agency,
                servers: agency.servers.map((server, sIdx) =>
                  sIdx !== selectedServerIndex
                    ? server
                    : {
                        ...server,
                        displays: server.displays.map((d, dIdx) => {
                          if (dIdx !== displayIndex) return d
                          
                          let nextDisplay = { ...d, [field]: value }
                          
                          // If screen_id changed, also update the screen_url to match its assignments
                          if (field === 'screen_id') {
                            const newScreen = screens.find(s => s._id === value)
                            if (newScreen) {
                              const sportsValue = (newScreen.assignments || []).join(',')
                              const currentUrl = d.screen_url || ''
                              
                              try {
                                // Use the enterprise base URL as a template for path and parameters
                                const template = baseUrl && isValidUrl(baseUrl) ? baseUrl : 'http://localhost'
                                const urlObj = new URL(template)
                                
                                // Maintain the protocol and host from the current URL if it's valid,
                                // otherwise keep those from the template.
                                if (currentUrl && isValidUrl(currentUrl)) {
                                  const currentUrlObj = new URL(currentUrl)
                                  urlObj.protocol = currentUrlObj.protocol
                                  urlObj.host = currentUrlObj.host
                                  urlObj.port = currentUrlObj.port
                                }
                                
                                if (sportsValue) {
                                  urlObj.searchParams.set('sports', sportsValue)
                                } else {
                                  urlObj.searchParams.delete('sports')
                                }
                                
                                nextDisplay.screen_url = decodeURIComponent(urlObj.toString())
                              } catch (e) {
                                // Fallback
                              }
                            }
                          }
                          
                          return nextDisplay
                        }),
                      },
                ),
              },
        ),
    )
  }

  /** Handle domain-only change for a display URL */
  const handleDisplayDomainChange = (displayIndex: number, newDomain: string) => {
    const currentUrl = selectedServer?.displays[displayIndex]?.screen_url || ''
    const currentSportsValue = getSportsFromUrl(currentUrl).join(',')

    // Only reconstruct the full URL if the domain looks like a valid origin
    const hasValidHost = /^https?:\/\/[^/\s]+/.test(newDomain)
    if (hasValidHost) {
      try {
        const template = baseUrl && isValidUrl(baseUrl) ? baseUrl : 'http://localhost'
        const urlObj = new URL(template)
        
        const newOriginObj = new URL(newDomain)
        urlObj.protocol = newOriginObj.protocol
        urlObj.host = newOriginObj.host
        urlObj.port = newOriginObj.port
        
        if (currentSportsValue) {
          urlObj.searchParams.set('sports', currentSportsValue)
        }
        
        handleDisplayChange(displayIndex, 'screen_url', decodeURIComponent(urlObj.toString()))
      } catch (e) {
        handleDisplayChange(displayIndex, 'screen_url', newDomain)
      }
    } else {
      handleDisplayChange(displayIndex, 'screen_url', newDomain)
    }
  }

  /** Append a new empty editable display row */
  const handleAddDisplay = () => {
    if (!selectedServer || !selectedAgency) return
    const defaultScreenId = screens.length > 0 ? screens[0]._id : ''
    const nextNum =
      selectedServer.displays.length > 0
        ? Math.max(...selectedServer.displays.map((d) => d.display)) + 1
        : 1
    const defaultUrl = baseUrl || ''
    const newDisplay = { display: nextNum, screen_url: defaultUrl, screen_id: defaultScreenId }
    updateAgencies(
      (prev) =>
        prev.map((agency) =>
          agency._id !== selectedAgencyId
            ? agency
            : {
                ...agency,
                servers: agency.servers.map((server, sIdx) =>
                  sIdx !== selectedServerIndex
                    ? server
                    : { ...server, displays: [...server.displays, newDisplay] },
                ),
              },
        ),
    )
  }

  const getSportsFromUrl = (url: string) => {
    try {
      const urlObj = new URL(url)
      const sports = urlObj.searchParams.get('sports') || urlObj.searchParams.get('SPORTS')
      return sports ? sports.split(',') : []
    } catch {
      const parts = url.split(/[?&]sports=|[?&]SPORTS=/i)
      return parts.length > 1 ? parts[1].split('&')[0].split(',') : []
    }
  }

  const filteredAgencies = agencyList
    .filter((a) => (a.name || '').toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))

  // ─── header style reused ───
  const headerCellSx = {
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    fontSize: '0.7rem',
    color: 'text.secondary',
  }

  return (
    <Box sx={{ display: 'flex', gap: 3, minHeight: 500 }}>

      {/* ── Left: Agencies ── */}
      <Box sx={{ minWidth: 180, maxWidth: 200 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1.5, mb: 1, display: 'block' }}>
          {label('SELECCIONAR_AGENCIA')}
        </Typography>
        <TextField
          size="small"
          placeholder={label('BUSCAR_AGENCIA')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ maxHeight: 600, overflowY: 'auto', pr: 0.5 }}>
          <List disablePadding>
            {filteredAgencies.map((agency) => {
              const isDirty = dirtySet.current.has(agency.id)
              return (
                <ListItemButton
                  key={agency.id}
                  selected={selectedAgencyId === agency.id}
                  onClick={() => handleAgencyClick(agency.id)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600, color: isDirty ? 'warning.main' : 'inherit' }}>
                        {agency.name}{isDirty ? ' *' : ''}
                      </Typography>
                    }
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => handleToggleAgency(e, agency.id)}
                    sx={{
                      p: 0.5,
                      color: (() => {
                        const local = agencies.find(a => a._id === agency.id)
                        if (local) return local.active !== false ? 'success.main' : 'text.disabled'
                        
                        const remote = allAgencyDisplays.current.find(a => a._id === agency.id)
                        if (!remote) return 'text.disabled'
                        return remote.active !== false ? 'success.main' : 'text.disabled'
                      })(),
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <PowerSettingsNewIcon fontSize="small" />
                  </IconButton>
                </ListItemButton>
              )
            })}
          </List>
        </Box>
      </Box>

      {/* ── Center: Servers ── */}
      <Box sx={{ minWidth: 220, maxWidth: 250 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1.5, mb: 1, display: 'block' }}>
          {label('SERVIDORES')}
        </Typography>
        <Stack spacing={1}>
          {selectedAgency?.servers.map((server, index) => {
            return (
            <Card
              key={index}
              variant="outlined"
              sx={{
                cursor: 'pointer',
                borderColor: selectedServerIndex === index ? 'primary.main' : 'divider',
                borderWidth: selectedServerIndex === index ? 2 : 1,
                opacity: server.active === false ? 0.6 : 1,
                '&:hover': { borderColor: 'primary.main' },
              }}
              onClick={() => setSelectedServerIndex(index)}
            >
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StorageIcon fontSize="small" color={selectedServerIndex === index ? 'primary' : 'action'} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {server.name || `Server #${index + 1}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {server.displays.length} Displays
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => handleToggleServer(e, index)}
                    sx={{
                      p: 0.5,
                      color: server.active !== false ? 'success.main' : 'text.disabled',
                      '&:hover': { bgcolor: server.active !== false ? 'success.lighter' : 'action.hover' },
                    }}
                  >
                    <PowerSettingsNewIcon fontSize="small" />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
            )
          })}
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            fullWidth
            disabled={!selectedAgencyId || loading}
            onClick={handleAddServer}
            sx={{ borderStyle: 'dashed', textTransform: 'none' }}
          >
            {label('NUEVO_SERVIDOR')}
          </Button>
        </Stack>
      </Box>

      {/* ── Right: Server details + Displays table ── */}
      <Box sx={{ flex: 1 }}>
        {selectedServer ? (
          <>
            {/* Server details card */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                      {label('NOMBRE_DEL_SERVIDOR')}
                    </Typography>
                    <TextField
                      key={`${selectedAgencyId}-${selectedServerIndex}-name`}
                      size="small"
                      fullWidth
                      value={selectedServer.name || ''}
                      placeholder={`Server #${selectedServerIndex + 1}`}
                      onChange={(e) => handleServerNameChange(e.target.value)}
                      error={!selectedServer.name?.trim()}
                      helperText={!selectedServer.name?.trim() ? 'El nombre del servidor es requerido' : ''}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <Box sx={{ width: 140 }}>
                    <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                      {label('HORARIO')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {(selectedAgency?.schedule?.startTime || enterpriseSchedule.startTime)
                        ? `${selectedAgency?.schedule?.startTime || enterpriseSchedule.startTime} - ${selectedAgency?.schedule?.endTime || enterpriseSchedule.endTime}`
                        : '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                      {label('TIMEZONE')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {selectedAgency?.schedule?.timezone || enterpriseSchedule.timezone || '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1.5 }}>
                    <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                      {label('LICENSE_KEY')}
                    </Typography>
                    <TextField
                      key={`${selectedAgencyId}-${selectedServerIndex}-key`}
                      size="small"
                      fullWidth
                      value={selectedServer.license_key || ''}
                      placeholder="License Key"
                      inputProps={{ readOnly: true }}
                      sx={{ 
                        mt: 0.5,
                        '& .MuiInputBase-input': { color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.75rem' },
                        '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(selectedServer.license_key || '')}
                              disabled={!selectedServer.license_key}
                            >
                              <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Tooltip title={label('TOOLTIP_CAMBIO_HARDWARE')}>
                      <span>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => setOpenConfirmDialog(true)}
                          disabled={disassociating || !selectedServer.license_key || !selectedServer.server_id}
                          sx={{ 
                            mt: 1.5,
                            textTransform: 'none',
                            bgcolor: '#132c3a',
                            color: '#ffffff',
                            p: '10px 24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            '&:hover': {
                              bgcolor: '#0a1d26',
                            },
                            '&.Mui-disabled': {
                              bgcolor: 'action.disabledBackground',
                              color: 'action.disabled',
                              opacity: 0.6,
                            },
                            transition: 'all 0.2s ease-in-out',
                            boxShadow: '0px 3px 6px rgba(0,0,0,0.16)',
                            minHeight: '52px',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textAlign: 'left' }}>
                            {disassociating ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <VpnKeyIcon sx={{ fontSize: 20, opacity: 0.9, color: '#ffffff' }} />
                            )}
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#ffffff', lineHeight: 1.2, fontSize: '0.85rem' }}>
                                {label('AUTORIZAR_CAMBIO_HARDWARE')}
                              </Typography>                              
                            </Box>
                          </Box>
                          <CheckIcon sx={{ fontSize: 18, opacity: 0.8, color: '#ffffff' }} />
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Agencia: {selectedAgency?.name}
                </Typography>
              </CardContent>
            </Card>

            {/* Displays table header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 2, fontSize: '0.75rem' }}>
                {label('DISPLAYS_EN_ESTE_SERVIDOR')}
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddDisplay}
                sx={{ textTransform: 'none' }}
              >
                {label('NUEVO_DISPLAY')}
              </Button>
            </Box>

            <TableContainer component={Card} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>{label('DISPLAY')}</TableCell>
                    {/* <TableCell sx={headerCellSx}>{label('URL')}</TableCell> */}
                    <TableCell sx={headerCellSx}>{label('SCREEN_ASIGNADO')}</TableCell>
                    <TableCell sx={headerCellSx}>{label('ASSIGNMENTS')}</TableCell>
                    <TableCell sx={headerCellSx} align="right">{label('ACCIONES')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedServer.displays.map((display, dIdx) => {
                    return (
                    <TableRow key={dIdx} sx={{ '&:last-child td': { borderBottom: 0 }, opacity: display.active === false ? 0.6 : 1 }}>

                      {/* Display # — editable */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MonitorIcon fontSize="small" color="action" />
                          <TextField
                            size="small"
                            type="number"
                            value={display.display}
                            onChange={(e) =>
                              handleDisplayChange(dIdx, 'display', Number(e.target.value))
                            }
                            inputProps={{ min: 1, style: { width: 30 } }}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
                          />
                        </Box>
                      </TableCell>

                      {/* URL — preview link */}
                      {/* <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Link
                            href={display.screen_url}
                            target="_blank"
                            rel="noopener"
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              textDecoration: 'none',
                              color: 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            <OpenInNewIcon sx={{ fontSize: 16 }} />
                            {label('VISTA_PREVIA_URL')}
                          </Link>
                        </Box>
                      </TableCell> */}

                      {/* Screen — Select */}
                      <TableCell>
                        <Select
                          size="small"
                          value={display.screen_id || ''}
                          onChange={(e) =>
                            handleDisplayChange(dIdx, 'screen_id', e.target.value as string)
                          }
                          fullWidth
                          sx={{ fontSize: '0.8rem' }}
                        >
                          {screens.map((screen) => (
                            <MenuItem key={screen._id} value={screen._id}>
                              {screen.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {(() => {
                            const selectedScreen = screens.find(s => s._id === display.screen_id)
                            const displayAssignments = selectedScreen?.assignments || []
                            
                            return displayAssignments.map((sport) => (
                              <Chip key={sport} label={sport} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                            ))
                          })()}
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveDisplay(dIdx)}
                          sx={{ '&:hover': { bgcolor: 'error.lighter' } }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>

                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">
              {label('SELECCIONAR_AGENCIA_O_SERVERS')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Overlays ── */}
      {(loading || loadingAgencies || loadingSharedData) && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.7)', zIndex: 10, borderRadius: 2 }}>
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ position: 'absolute', top: 20, right: 20, zIndex: 11, boxShadow: 3 }}>
          {error}
        </Alert>
      )}

      <Snackbar open={saveSuccess} autoHideDuration={3000} onClose={() => setSaveSuccess(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <MuiAlert severity="success" variant="filled" onClose={() => setSaveSuccess(false)}>
          {label('CAMBIOS_GUARDADOS')}
        </MuiAlert>
      </Snackbar>

      {/* Confirmation Dialog for Hardware Change */}
      <Dialog 
        open={openConfirmDialog} 
        onClose={() => setOpenConfirmDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {label('CONFIRM_CAMBIO_HARDWARE_TITULO')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {label('CONFIRM_CAMBIO_HARDWARE_MENSAJE')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setOpenConfirmDialog(false)} 
            color="inherit" 
            sx={{ textTransform: 'none' }}
          >
            {label('NO')}
          </Button>
          <Button 
            onClick={() => {
              setOpenConfirmDialog(false)
              handleDisassociateServer()
            }} 
            variant="contained"
            color="error"
            sx={{ textTransform: 'none' }}
            autoFocus
          >
            {label('SI')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AsociarDisplays
